// Token validation utilities
import { jwtDecode } from "jwt-decode";

export const TOKEN_KEYS = {
  ADMIN_ACCESS: 'adminAccessToken',
  ADMIN_REFRESH: 'adminRefreshToken',
  ADMIN_INFO: 'adminInfo',
  USER_ACCESS: 'userAccessToken', 
  USER_REFRESH: 'userRefreshToken',
  USER_INFO: 'user',
  LAST_ACTIVITY: 'lastActivity'
};

export const IDLE_LIMIT_SECONDS = 900; // 15 minutes

/**
 * Safely decode JWT token
 * @param {string} token - JWT token
 * @returns {object|null} - Decoded token or null if invalid
 */
export const safeDecodeToken = (token) => {
  if (!token || typeof token !== 'string') return null;
  
  try {
    return jwtDecode(token);
  } catch (error) {
    console.warn('Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} - True if expired or invalid
 */
export const isTokenExpired = (token) => {
  const decoded = safeDecodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const now = Date.now() / 1000;
  return decoded.exp <= now;
};

/**
 * Check if user has been idle for too long
 * @returns {boolean} - True if idle time exceeds limit
 */
export const isUserIdle = () => {
  const lastActivity = parseInt(localStorage.getItem(TOKEN_KEYS.LAST_ACTIVITY), 10);
  if (!lastActivity) return true;
  
  const nowMs = Date.now();
  const idleSeconds = (nowMs - lastActivity) / 1000;
  return idleSeconds > IDLE_LIMIT_SECONDS;
};

/**
 * Update user activity timestamp
 */
export const updateActivity = () => {
  localStorage.setItem(TOKEN_KEYS.LAST_ACTIVITY, Date.now().toString());
};

/**
 * Check if admin is authenticated with valid token
 * @returns {object} - Authentication status and token info
 */
export const checkAdminAuth = () => {
  const accessToken = localStorage.getItem(TOKEN_KEYS.ADMIN_ACCESS);
  const refreshToken = localStorage.getItem(TOKEN_KEYS.ADMIN_REFRESH);
  
  if (!accessToken) {
    return {
      isAuthenticated: false,
      needsRefresh: false,
      reason: 'NO_ACCESS_TOKEN'
    };
  }
  
  const isAccessExpired = isTokenExpired(accessToken);
  const isIdle = isUserIdle();
  
  if (isAccessExpired && !refreshToken) {
    return {
      isAuthenticated: false,
      needsRefresh: false,
      reason: 'NO_REFRESH_TOKEN'
    };
  }
  
  if (isAccessExpired && isIdle) {
    return {
      isAuthenticated: false,
      needsRefresh: false,
      reason: 'USER_IDLE'
    };
  }
  
  if (isAccessExpired && refreshToken && !isIdle) {
    return {
      isAuthenticated: false,
      needsRefresh: true,
      reason: 'ACCESS_EXPIRED'
    };
  }
  
  // Access token is valid
  return {
    isAuthenticated: true,
    needsRefresh: false,
    reason: 'VALID_TOKEN'
  };
};

/**
 * Clear all admin authentication data
 */
export const clearAdminAuth = () => {
  localStorage.removeItem(TOKEN_KEYS.ADMIN_ACCESS);
  localStorage.removeItem(TOKEN_KEYS.ADMIN_REFRESH);
  localStorage.removeItem(TOKEN_KEYS.ADMIN_INFO);
  localStorage.removeItem(TOKEN_KEYS.LAST_ACTIVITY);
};

/**
 * Clear all user authentication data
 */
export const clearUserAuth = () => {
  localStorage.removeItem(TOKEN_KEYS.USER_ACCESS);
  localStorage.removeItem(TOKEN_KEYS.USER_REFRESH);
  localStorage.removeItem(TOKEN_KEYS.USER_INFO);
  localStorage.removeItem(TOKEN_KEYS.LAST_ACTIVITY);
};

/**
 * Get decoded admin info from token
 * @returns {object|null} - Admin info or null
 */
export const getAdminInfo = () => {
  const token = localStorage.getItem(TOKEN_KEYS.ADMIN_ACCESS);
  if (!token) return null;
  
  const decoded = safeDecodeToken(token);
  return decoded ? decoded : null;
};

/**
 * Check if user is authenticated with valid token
 * @returns {object} - Authentication status and token info
 */
export const checkUserAuth = () => {
  const accessToken = localStorage.getItem(TOKEN_KEYS.USER_ACCESS);
  const refreshToken = localStorage.getItem(TOKEN_KEYS.USER_REFRESH);
  
  if (!accessToken) {
    return {
      isAuthenticated: false,
      needsRefresh: false,
      reason: 'NO_ACCESS_TOKEN'
    };
  }
  
  const isAccessExpired = isTokenExpired(accessToken);
  const isIdle = isUserIdle();
  
  if (isAccessExpired && !refreshToken) {
    return {
      isAuthenticated: false,
      needsRefresh: false,
      reason: 'NO_REFRESH_TOKEN'
    };
  }
  
  if (isAccessExpired && isIdle) {
    return {
      isAuthenticated: false,
      needsRefresh: false,
      reason: 'USER_IDLE'
    };
  }
  
  if (isAccessExpired && refreshToken && !isIdle) {
    return {
      isAuthenticated: false,
      needsRefresh: true,
      reason: 'ACCESS_EXPIRED'
    };
  }
  
  // Access token is valid
  return {
    isAuthenticated: true,
    needsRefresh: false,
    reason: 'VALID_TOKEN'
  };
};

/**
 * Get decoded user info from token
 * @returns {object|null} - User info or null
 */
export const getUserInfo = () => {
  const token = localStorage.getItem(TOKEN_KEYS.USER_ACCESS);
  if (!token) return null;
  
  const decoded = safeDecodeToken(token);
  return decoded ? decoded : null;
};

/**
 * Check if token will expire soon (within 5 minutes)
 * @param {string} token - JWT token
 * @returns {boolean} - True if expiring soon
 */
export const isTokenExpiringSoon = (token) => {
  const decoded = safeDecodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const now = Date.now() / 1000;
  const fiveMinutes = 5 * 60; // 5 minutes in seconds
  return (decoded.exp - now) <= fiveMinutes;
};